import type { CreativeFormatId } from './creative.js';
import type { DirectorForm } from './creative-director.js';

/**
 * BECERİ KATALOĞU — sohbetin GİRİŞ KAPISI (21 Ağu 2026).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden var: "boş kutu" sorunu
 * ═══════════════════════════════════════════════════════════════════════════
 * Kreatif direktörü ilk hâlinde boş bir sohbet kutusuyla açılıyordu ve
 * kullanıcının cevaplaması gereken ilk soru şuydu: "ne yazmalıyım?". Bu, en
 * pahalı soru — çünkü cevabı bilen kullanıcının zaten sohbete ihtiyacı yok,
 * bilmeyen ise kutuya bakıp kapatıyor.
 *
 * Beceri kataloğu o soruyu ORTADAN KALDIRIYOR: kullanıcı yazmıyor, NE
 * YAPMAK İSTEDİĞİNİ SEÇİYOR. Yazmak zorunda olduğu tek şey, direktörün
 * soracağı soruların cevabı — ve onlar da şıklı geliyor.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 KATALOG ÜRÜNÜN GERÇEKTEN YAPABİLDİKLERİ — vaat listesi DEĞİL
 * ═══════════════════════════════════════════════════════════════════════════
 * Her beceri var olan bir üretim yoluna karşılık geliyor: bir `CreativeType`,
 * bir sekme ve o sekmenin alanları. Yapamadığımız bir şeye kart açmak
 * (karusel, montaj, altyazı) kullanıcıyı tıklayıp "yakında" duvarına
 * çarptırmak olurdu.
 *
 * ⚠️ `preview` ALANI GERÇEK ÇIKTI. Kartın görseli `apps/web/public/templates/`
 * altındaki dosyalardan biri ve o dosyalar sahne şablonlarının KENDİ
 * reçeteleriyle üretildi (`scene-templates.ts` → `preview` notu). Yani kartta
 * duran şey el yapımı bir mockup değil, bu ürünün gerçekten ürettiği bir
 * görsel. Video becerilerinde de görsel kullanılıyor: bir video karesi de
 * sonuçta bir görsel ve sahte bir oynatıcı kabuğu çizmek, olmayan bir
 * önizlemeyi varmış gibi göstermek olurdu.
 */

export const SKILL_GROUPS = ['image', 'video'] as const;
export type SkillGroup = (typeof SKILL_GROUPS)[number];

export interface CreativeSkillCard {
  id: string;
  group: SkillGroup;
  name: { tr: string; en: string };
  /// Kartın altındaki tek satır — ne işe yaradığı, nasıl çalıştığı değil.
  description: { tr: string; en: string };
  /// `apps/web/public/` altında mutlak yol.
  preview: string;
  /**
   * Bu beceri ÜRÜN GÖRSELİ İSTİYOR mu.
   *
   * `true` ise arayüz beceriyi seçtiren adımdan sonra ürünü SORUYOR ve
   * kullanıcı yüklemeden ilerleyemiyor. Sohbete "hangi ürün" diye sordurmak
   * da mümkündü ve yapılmadı: bir dosya seçtirmek bir LLM turuna değmez ve
   * cevabı yazıyla alınamaz.
   */
  needsProduct: boolean;
}

/**
 * Becerinin SUNUCUYA AİT yarısı.
 *
 * `decides` modele "bu beceri için neyin kararlaştırılması gerekiyor"
 * diyor — soruları o listeden TÜRETİYOR. Soruların kendisi burada YAZILI
 * DEĞİL ve bu, özelliğin can alıcı noktası: sabit bir soru listesi her ürüne
 * aynı şeyi sorardı ("hedef kitlen kim?"), oysa bir abajura sorulacak şeyle
 * bir yüz serumuna sorulacak şey farklı. Model ürünü GÖRÜYOR (görü künyesi
 * prompt'a giriyor) ve soruyu ona göre kuruyor.
 */
export interface CreativeSkillRecipe {
  /// Bu beceri seçilir seçilmez forma yazılan başlangıç değerleri.
  defaults: DirectorForm & { kind?: 'image' | 'video' };
  /// Modelin karara bağlaması gereken şeyler — soruların kaynağı.
  decides: string[];
  /// Modele becerinin ne olduğunu anlatan tek paragraf.
  brief: string;
}

const SQUARE: CreativeFormatId = 'feed_square';
const PORTRAIT: CreativeFormatId = 'feed_portrait';

export const CREATIVE_SKILLS: readonly CreativeSkillCard[] = [
  // ── Reklam görselleri ────────────────────────────────────────────────────
  {
    id: 'product-shot',
    group: 'image',
    name: { tr: 'Ürün vitrini', en: 'Product shot' },
    description: {
      tr: 'Ürün kahraman, sahne sade. Katalog ve retargeting için en güvenlisi.',
      en: 'Product as hero, scene kept quiet. The safest pick for catalogue and retargeting.',
    },
    preview: '/templates/pastel-props-studio.webp',
    needsProduct: true,
  },
  {
    id: 'lifestyle-shot',
    group: 'image',
    name: { tr: 'Yaşam sahnesi', en: 'Lifestyle scene' },
    description: {
      tr: 'Ürün kullanıldığı ortamda. Alıcının sahip olmayı hayal etmesi gerektiğinde.',
      en: 'The product in the room it belongs to — for when the buyer has to imagine owning it.',
    },
    preview: '/templates/mint-bathroom-counter.webp',
    needsProduct: true,
  },
  {
    id: 'model-shot',
    group: 'image',
    name: { tr: 'Mankenli tanıtım', en: 'Model shot' },
    description: {
      tr: 'Ürünü bir insan giyiyor, tutuyor ya da kullanıyor. Akışta durdurma gücü en yüksek olan.',
      en: 'A person wearing, holding or using the product — the highest stopping power in a feed.',
    },
    preview: '/templates/straw-bag-summer.webp',
    needsProduct: true,
  },
  {
    id: 'promo-post',
    group: 'image',
    name: { tr: 'Kampanya postu', en: 'Promo post' },
    description: {
      tr: 'İndirim, sezon sonu, son gün. Rakam ve aciliyet taşıyan hazır düzenler.',
      en: 'Discounts, season end, last day — layouts built to carry a number and a deadline.',
    },
    preview: '/templates/desert-speed-bar.webp',
    needsProduct: true,
  },

  // ── Reklam videoları ─────────────────────────────────────────────────────
  {
    id: 'product-video',
    group: 'video',
    name: { tr: 'Ürün videosu', en: 'Product video' },
    description: {
      tr: 'Fotoğrafından çekim planı çıkarılır: sahne, ışık, kamera hareketi ve müzik.',
      en: 'A shot plan drawn from your photo: setting, light, camera movement and music.',
    },
    preview: '/templates/arc-light-tunnel.webp',
    needsProduct: true,
  },
  {
    id: 'ugc-video',
    group: 'video',
    name: { tr: 'UGC · konuşan sunucu', en: 'UGC · talking presenter' },
    description: {
      tr: 'Bir kişi kameraya ürünü anlatıyor. Sosyal kanıt ve tavsiye dili.',
      en: 'A person telling the camera about the product — social proof and recommendation.',
    },
    preview: '/templates/hand-to-hand.webp',
    needsProduct: true,
  },
  {
    id: 'template-video',
    group: 'video',
    name: { tr: 'Hazır video şablonu', en: 'Video template' },
    description: {
      tr: 'Kataloğumuzdan bir kurgu seç, ürününü içine koy. En hızlı yol.',
      en: 'Pick a ready cut from our catalogue and drop your product into it. The fastest route.',
    },
    preview: '/templates/speed-trail-studio.webp',
    needsProduct: true,
  },
];

export function findSkill(id: string): CreativeSkillCard | null {
  return CREATIVE_SKILLS.find((skill) => skill.id === id) ?? null;
}

/**
 * REÇETELER — becerinin sunucuya ait yarısı.
 *
 * ⚠️ Bu tablo `index.ts` üzerinden DIŞARI ÇIKIYOR ve çıkması sorun değil:
 * içinde bir prompt YOK, yalnız varsayılan form değerleri ve modele
 * verilecek tek paragraflık bir tarif var. Sahne reçetelerinden farkı bu —
 * orada üç bin karakterlik yönergeler vardı ve onlar alt yolda kaldı.
 */
export const CREATIVE_SKILL_RECIPES: Record<string, CreativeSkillRecipe> = {
  'product-shot': {
    defaults: { kind: 'image', mode: 'product', formatId: SQUARE, presenterModelId: null },
    decides: [
      'the surface and background the product sits on',
      'the light: soft and even, or hard and directional',
      'whether the frame is a straight-on hero or a three-quarter angle',
    ],
    brief:
      'A clean catalogue-grade shot. The product owns the frame, the scene supports it and never competes. No person unless the merchant asks for one.',
  },
  'lifestyle-shot': {
    defaults: { kind: 'image', mode: 'product', formatId: PORTRAIT, presenterModelId: null },
    decides: [
      'the room or place the product lives in',
      'the time of day and the quality of light',
      'what else is in frame — and how little of it there can be',
    ],
    brief:
      'The product shown where it is actually used. Context does the selling: the buyer should picture it in their own space. Clutter is the enemy — one or two props at most.',
  },
  'model-shot': {
    defaults: { kind: 'image', mode: 'product', formatId: PORTRAIT },
    decides: [
      'which model from the cast fits the product and the brand',
      'what the person does with the product — wear, hold, use, or let the renderer decide',
      'the setting the person is in',
    ],
    brief:
      'A person presents the product. The person raises stopping power but stays in service of the object: the product sits in the light, the model supports it. You MUST set presenterModelId for this skill.',
  },
  'promo-post': {
    defaults: { kind: 'image', mode: 'product', formatId: SQUARE },
    decides: [
      'the offer itself — what the number is and what it applies to',
      'the deadline or scarcity, if there is one',
      'which scene template carries a number best',
    ],
    brief:
      'A campaign post. One claim, readable in half a second. Pick a scene template from the promo category — those layouts are built to carry a number. Never invent a discount the merchant did not state.',
  },
  'product-video': {
    defaults: { kind: 'video', mode: 'product' },
    decides: [
      'the feeling the video should leave',
      'the setting and the time of day',
      'whether the product is shown alone or in use',
      'the length and whether it runs with sound',
    ],
    brief:
      "A product film built from the merchant's own photo. The shot plan itself is produced by the vision analysis on the workbench — your job is to settle the direction and write it into the brief so the analysis reads it.",
  },
  'ugc-video': {
    defaults: { kind: 'video', mode: 'ugc' },
    decides: [
      'who is speaking and where they are',
      'the one thing they say about the product',
      'the language and the voice',
    ],
    brief:
      "A talking-presenter video. It has to sound like a person who bought the thing, not an ad. One claim, said plainly. Write the script into the brief in the merchant's language.",
  },
  'template-video': {
    defaults: { kind: 'video', mode: 'template' },
    decides: ['which template in the catalogue matches the product and the message'],
    brief:
      'A ready cut from the catalogue. Pick the template whose shape fits the product; the merchant only supplies the product photo.',
  },
};
